import { useLayoutEffect, useState } from 'react';
const MAX_LEVEL = 3;
/**
 * How far the row's last child (the trailing controls) runs past the row's
 * content box, in px. scrollWidth alone misses controls that only eat into
 * the end padding.
 */
function overflowOf(row) {
    const last = row.lastElementChild;
    if (!last)
        return 0;
    const paddingEnd = parseFloat(getComputedStyle(row).paddingRight) || 0;
    return last.getBoundingClientRect().right - (row.getBoundingClientRect().right - paddingEnd);
}
/**
 * Measures the header row and steps up {@link HeaderFitLevel} until nothing
 * overflows. English fits every width from lg, but German, French and Spanish
 * labels (and the region switcher) pushed the account menu up to 260px past
 * the viewport between 1024 and 1280px, which panned the whole page sideways.
 * Measuring rather than guessing a breakpoint keeps the full header wherever
 * it fits, whatever the language or the set of controls a user has.
 *
 * `contentKey` must change whenever the row's natural width can change
 * (language, module links, breakpoint tier, account label): the fit then
 * restarts from the full row before paint. Width changes no key can name (a
 * web font arriving, a control that appears after a fetch) restart it too,
 * from a ResizeObserver on the row's content. Each level remembers the width
 * it overflowed at, so widening the window only brings a level back once it
 * fits again, which keeps it from flickering between two levels.
 */
export function useHeaderFit(rowRef, contentKey) {
    const [state, setState] = useState({ key: contentKey, level: 0, needed: [] });
    // New content: start again from the full row (React's reset-on-prop pattern).
    if (state.key !== contentKey)
        setState({ key: contentKey, level: 0, needed: [] });
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row)
            return;
        const fit = () => {
            const available = row.clientWidth;
            // Not laid out yet (or jsdom): nothing to measure.
            if (available === 0)
                return;
            const overflow = overflowOf(row);
            // Half a pixel of slack for sub-pixel layout.
            if (overflow > 0.5) {
                if (state.level < MAX_LEVEL) {
                    const needed = state.needed.slice(0, state.level);
                    needed[state.level] = available + overflow;
                    setState({ ...state, level: (state.level + 1), needed });
                }
            }
            else if (state.level > 0 && available >= state.needed[state.level - 1]) {
                setState({ ...state, level: (state.level - 1) });
            }
        };
        fit();
        if (typeof ResizeObserver === 'undefined')
            return;
        // The row resizes with the window. Its content children (not a flex-grow
        // spacer, which only tracks the slack) resize when their content does: a
        // web font arriving, a control appearing or leaving after a fetch. Their
        // widths are taken now, at this level; this effect re-runs on every level
        // change, so a later difference is never the fit's own doing.
        const content = Array.from(row.children).filter((child) => !(parseFloat(getComputedStyle(child).flexGrow) > 0));
        const widths = () => content.map((child) => child.getBoundingClientRect().width);
        const settled = widths();
        const observer = new ResizeObserver(() => {
            const contentChanged = widths().some((w, i) => Math.abs(w - settled[i]) > 0.5);
            // The widths remembered for the levels below were measured with the old
            // content, so they would bring a level back too early (content grew) or
            // never (it shrank). Fit again from the full row, as a key change does.
            if (contentChanged && state.level > 0)
                setState({ key: state.key, level: 0, needed: [] });
            else
                fit();
        });
        observer.observe(row);
        for (const child of content)
            observer.observe(child);
        return () => observer.disconnect();
    }, [rowRef, state]);
    return state.key === contentKey ? state.level : 0;
}
